import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate, useSearchParams } from "react-router-dom";

import { usePlaylists, useDeletePlaylist } from "@/hooks/usePlaylists";
import { useCreatePlaylistShare } from "@/hooks/useSharing";
import { ConfirmDialog } from "@/components/dashboard/ConfirmDialog";
import { useAuth, useIsAdmin } from "@/lib/auth";
import { buildSharePlaylistUrl, copyToClipboard } from "@/lib/helpers";
import { useAppStore } from "@/lib/store";
import { cn } from "@/lib/utils";

const logoDark = "/logo-dark.png";

/* ── Phosphor-style icons (matching Webflow production) ─────────────── */

function MyLibraryIcon({ className }: { className?: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M16.75 3.25C16.9489 3.25 17.1397 3.32902 17.2803 3.46967C17.421 3.61032 17.5 3.80109 17.5 4V16C17.5 16.1989 17.421 16.3897 17.2803 16.5303C17.1397 16.671 16.9489 16.75 16.75 16.75H3.25C3.05109 16.75 2.86032 16.671 2.71967 16.5303C2.57902 16.3897 2.5 16.1989 2.5 16V4C2.5 3.80109 2.57902 3.61032 2.71967 3.46967C2.86032 3.32902 3.05109 3.25 3.25 3.25H16.75ZM9.25 10.75H4V15.25H9.25V10.75ZM16 10.75H10.75V15.25H16V10.75ZM9.25 4.75H4V9.25H9.25V4.75ZM16 4.75H10.75V9.25H16V4.75Z" fill="currentColor" />
    </svg>
  );
}

function AddOnsIcon({ className }: { className?: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path d="M16.25 6.875H3.75C3.41848 6.875 3.10054 7.0067 2.86612 7.24112C2.6317 7.47554 2.5 7.79348 2.5 8.125V15.625C2.5 15.9565 2.6317 16.2745 2.86612 16.5089C3.10054 16.7433 3.41848 16.875 3.75 16.875H16.25C16.5815 16.875 16.8995 16.7433 17.1339 16.5089C17.3683 16.2745 17.5 15.9565 17.5 15.625V8.125C17.5 7.79348 17.3683 7.47554 17.1339 7.24112C16.8995 7.0067 16.5815 6.875 16.25 6.875ZM16.25 15.625H3.75V8.125H16.25V15.625ZM3.75 5C3.75 4.83424 3.81585 4.67527 3.93306 4.55806C4.05027 4.44085 4.20924 4.375 4.375 4.375H15.625C15.7908 4.375 15.9497 4.44085 16.0669 4.55806C16.1842 4.67527 16.25 4.83424 16.25 5C16.25 5.16576 16.1842 5.32473 16.0669 5.44194C15.9497 5.55915 15.7908 5.625 15.625 5.625H4.375C4.20924 5.625 4.05027 5.55915 3.93306 5.44194C3.81585 5.32473 3.75 5.16576 3.75 5ZM5 2.5C5 2.33424 5.06585 2.17527 5.18306 2.05806C5.30027 1.94085 5.45924 1.875 5.625 1.875H14.375C14.5408 1.875 14.6997 1.94085 14.8169 2.05806C14.9342 2.17527 15 2.33424 15 2.5C15 2.66576 14.9342 2.82473 14.8169 2.94194C14.6997 3.05915 14.5408 3.125 14.375 3.125H5.625C5.45924 3.125 5.30027 3.05915 5.18306 2.94194C5.06585 2.82473 5 2.66576 5 2.5Z" fill="currentColor" />
    </svg>
  );
}

function GearIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className={className}>
      <path d="M128,80a48,48,0,1,0,48,48A48.05,48.05,0,0,0,128,80Zm0,80a32,32,0,1,1,32-32A32,32,0,0,1,128,160Zm88-29.84q.06-2.16,0-4.32l14.92-18.64a8,8,0,0,0,1.48-7.06,107.21,107.21,0,0,0-10.88-26.25,8,8,0,0,0-6-3.93l-23.72-2.64q-1.48-1.56-3-3L186,40.54a8,8,0,0,0-3.94-6,107.71,107.71,0,0,0-26.25-10.87,8,8,0,0,0-7.06,1.49L130.16,40Q128,40,125.84,40L107.2,25.11a8,8,0,0,0-7.06-1.48A107.6,107.6,0,0,0,73.89,34.51a8,8,0,0,0-3.93,6L67.32,64.27q-1.56,1.49-3,3L40.54,70a8,8,0,0,0-6,3.94,107.71,107.71,0,0,0-10.87,26.25,8,8,0,0,0,1.49,7.06L40,125.84Q40,128,40,130.16L25.11,148.8a8,8,0,0,0-1.48,7.06,107.21,107.21,0,0,0,10.88,26.25,8,8,0,0,0,6,3.93l23.72,2.64q1.49,1.56,3,3L70,215.46a8,8,0,0,0,3.94,6,107.71,107.71,0,0,0,26.25,10.87,8,8,0,0,0,7.06-1.49L125.84,216q2.16.06,4.32,0l18.64,14.92a8,8,0,0,0,7.06,1.48,107.21,107.21,0,0,0,26.25-10.88,8,8,0,0,0,3.93-6l2.64-23.72q1.56-1.48,3-3L215.46,186a8,8,0,0,0,6-3.94,107.71,107.71,0,0,0,10.87-26.25,8,8,0,0,0-1.49-7.06Zm-16.1-6.5a73.93,73.93,0,0,1,0,8.68,8,8,0,0,0,1.74,5.68l14.19,17.73a91.57,91.57,0,0,1-6.23,15L187.11,168a8,8,0,0,0-5.1,2.64,74.11,74.11,0,0,1-6.14,6.14,8,8,0,0,0-2.64,5.1l-2.51,22.58a91.32,91.32,0,0,1-15,6.23l-17.74-14.19a8,8,0,0,0-5-1.75h-.67a73.68,73.68,0,0,1-8.68,0,8,8,0,0,0-5.68,1.74L100.45,210.7a91.57,91.57,0,0,1-15-6.23L82.71,182a8,8,0,0,0-2.64-5.1,74.11,74.11,0,0,1-6.14-6.14,8,8,0,0,0-5.1-2.64L46.25,165.6a91.32,91.32,0,0,1-6.23-15l14.19-17.74a8,8,0,0,0,1.74-5.68,73.93,73.93,0,0,1,0-8.68,8,8,0,0,0-1.74-5.68L40.02,95.07a91.57,91.57,0,0,1,6.23-15L68.89,82.71a8,8,0,0,0,5.1-2.64,74.11,74.11,0,0,1,6.14-6.14A8,8,0,0,0,82.77,68.83L85.28,46.25a91.32,91.32,0,0,1,15-6.23l17.74,14.19a8,8,0,0,0,5.68,1.74,73.68,73.68,0,0,1,8.68,0,8,8,0,0,0,5.68-1.74L155.82,40.02a91.57,91.57,0,0,1,15,6.23L173.29,68.83a8,8,0,0,0,2.64,5.1,74.11,74.11,0,0,1,6.14,6.14,8,8,0,0,0,5.1,2.64l22.58,2.51a91.32,91.32,0,0,1,6.23,15l-14.19,17.74A8,8,0,0,0,199.9,123.66Z" />
    </svg>
  );
}

function SignOutIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className={className}>
      <path d="M112,216a8,8,0,0,1-8,8H48a16,16,0,0,1-16-16V48A16,16,0,0,1,48,32h56a8,8,0,0,1,0,16H48V208h56A8,8,0,0,1,112,216Zm117.66-93.66-40-40a8,8,0,0,0-11.32,11.32L204.69,120H104a8,8,0,0,0,0,16h100.69l-26.35,26.34a8,8,0,0,0,11.32,11.32l40-40A8,8,0,0,0,229.66,122.34Z" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className={className}>
      <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z" />
    </svg>
  );
}

function CaretRightIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className={className}>
      <path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z" />
    </svg>
  );
}

function DotsThreeIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className={className}>
      <path d="M112,60a16,16,0,1,1,16,16A16,16,0,0,1,112,60Zm16,52a16,16,0,1,0,16,16A16,16,0,0,0,128,112Zm0,68a16,16,0,1,0,16,16A16,16,0,0,0,128,180Z" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor" className={className}>
      <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z" />
    </svg>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor" className={className}>
      <path d="M212,200a36,36,0,1,1-69.85-12.19l-53.3-30.75a36,36,0,1,1,0-58.12l53.3-30.75a36.09,36.09,0,1,1,8,13.86l-53.3,30.75a36.15,36.15,0,0,1,0,30.4l53.3,30.75A36,36,0,0,1,212,200Z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor" className={className}>
      <path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z" />
    </svg>
  );
}

/* ── Playlist context menu ─────────────────────────────────────────── */

function PlaylistContextMenu({
  onClose,
  onDeleteRequest,
  onShareRequest,
}: {
  onClose: () => void;
  onDeleteRequest: () => void;
  onShareRequest: () => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      className="absolute right-0 top-full z-50 mt-1 w-[10rem] rounded-[0.5rem] border border-[#e2e4e9] bg-white py-1 shadow-lg"
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[0.8125rem] text-[#525866] hover:bg-[#f6f8fa] transition-colors"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onShareRequest();
        }}
      >
        <ShareIcon />
        Share
      </button>
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[0.8125rem] text-[#ba4d4d] hover:bg-[#fef2f2] transition-colors"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDeleteRequest();
        }}
      >
        <TrashIcon className="text-[#ba4d4d]" />
        Delete
      </button>
    </div>
  );
}

/* ── Navigation config ────────────────────────────────────────────────── */

const primaryNav = [
  { label: "My Library", to: "/app/dashboard/home", Icon: MyLibraryIcon },
  { label: "Add Ons", to: "/app/dashboard/add-ons", Icon: AddOnsIcon },
];

const utilityNav = [
  { label: "Settings", to: "/app/dashboard/settings", Icon: GearIcon },
];

function HamburgerIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className={className}>
      <path d="M224,128a8,8,0,0,1-8,8H40a8,8,0,0,1,0-16H216A8,8,0,0,1,224,128ZM40,72H216a8,8,0,0,0,0-16H40a8,8,0,0,0,0,16ZM216,184H40a8,8,0,0,0,0,16H216a8,8,0,0,0,0-16Z" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className={className}>
      <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" />
    </svg>
  );
}

export function Sidebar() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activePlaylistId = searchParams.get("playlist_id");
  const { user, signOut } = useAuth();
  const isAdmin = useIsAdmin();
  const { openModal } = useAppStore();
  const { data: playlists, setData: setPlaylists } = usePlaylists();
  const { execute: createPlaylistShare } = useCreatePlaylistShare();
  const { execute: deletePlaylist } = useDeletePlaylist();
  const [loggingOut, setLoggingOut] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [copiedShareId, setCopiedShareId] = useState<string | null>(null);

  useEffect(() => {
    function handlePlaylistRenamed(event: Event) {
      const detail = (event as CustomEvent<{ id?: string; name?: string }>).detail;
      if (!detail?.id || !detail.name) return;

      const { id, name } = detail;
      setPlaylists((prev) =>
        prev
          ? prev.map((playlist) =>
              playlist.id === id ? { ...playlist, name } : playlist,
            )
          : prev,
      );
    }

    window.addEventListener("bollyrhythm:playlist-renamed", handlePlaylistRenamed);
    return () => window.removeEventListener("bollyrhythm:playlist-renamed", handlePlaylistRenamed);
  }, [setPlaylists]);

  const handleDeletePlaylist = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    await deletePlaylist(deleteTarget.id);
    setDeleteLoading(false);
    setDeleteTarget(null);
    navigate("/app/dashboard/home#performances");
  };

  const handleSharePlaylist = async (playlistId: string) => {
    if (!user) return;

    // Always go through the mutation so cached sidebar data cannot copy a
    // stale/deleted token and existing links get their expiry refreshed.
    const result = await createPlaylistShare(playlistId);
    const invitationToken = result.data?.invitation_token;
    setOpenMenuId(null);
    if (!invitationToken) return;

    const copied = await copyToClipboard(buildSharePlaylistUrl(invitationToken));
    if (copied) {
      setCopiedShareId(playlistId);
      setTimeout(() => setCopiedShareId(null), 2000);
    }
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    await signOut();
    navigate("/");
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      {/* Mobile top bar with logo + hamburger */}
      <div className="hidden max-[991px]:flex items-center justify-between border-b border-[#e2e4e9] bg-white px-4 min-h-[4rem]">
        <Link to="/" onClick={closeMobile}>
          <img src={logoDark} alt="BollyRhythm" className="w-[7rem] h-auto" />
        </Link>
        <button
          type="button"
          className="inline-flex items-center justify-center p-1 text-[#241d22]"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          onClick={() => setMobileOpen((prev) => !prev)}
        >
          {mobileOpen ? (
            <XIcon className="h-6 w-6" />
          ) : (
            <HamburgerIcon className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Backdrop overlay on mobile */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 hidden max-[991px]:block"
          onClick={closeMobile}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "flex w-[19.5rem] min-w-[19.5rem] flex-col border-r border-[#e2e4e9] bg-white min-[992px]:sticky min-[992px]:top-0 min-[992px]:h-screen min-[992px]:overflow-hidden",
          "max-[991px]:fixed max-[991px]:inset-y-0 max-[991px]:left-0 max-[991px]:z-50 max-[991px]:w-[17rem] max-[991px]:min-w-0 max-[991px]:shadow-xl max-[991px]:transition-transform max-[991px]:duration-200 max-[991px]:ease-in-out",
          mobileOpen ? "max-[991px]:translate-x-0" : "max-[991px]:-translate-x-full"
        )}
      >
        <Link
          to="/"
          className="flex min-h-[4.5rem] items-center border-b border-[#e2e4e9] px-5"
          onClick={closeMobile}
        >
          <img src={logoDark} alt="BollyRhythm" className="w-[8rem] h-auto" />
        </Link>

        <div className="flex-1 px-5 pt-5 overflow-y-auto">
          <section className="mb-5">
            <div className="flex w-full items-center gap-3 py-2 pr-2">
              <div className="text-[#868c98] text-[0.75rem] uppercase">
                Dashboard
              </div>
            </div>

            <nav className="grid">
              {primaryNav.map((item) => (
                <NavLink
                  key={item.label}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center justify-between rounded-[0.5rem] pr-2 text-black text-[0.875rem] font-medium transition-[background-color,color] duration-[180ms] ease-in-out hover:bg-[#f6f8fa]",
                      isActive && "bg-[#f6f8fa]"
                    )
                  }
                  onClick={closeMobile}
                >
                  {({ isActive }) => (
                    <>
                      <span className="inline-flex items-center gap-3 py-2 pl-2">
                        <item.Icon className={cn("h-5 w-5 text-[#525866]", isActive && "text-[#762a5c]")} />
                        <span>{item.label}</span>
                      </span>
                      <CaretRightIcon className="ml-auto h-5 w-5 text-[#525866]" />
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          </section>

          <section className="mb-5">
            <div className="flex w-full items-center justify-start gap-3 py-2 pr-2">
              <span className="text-[#868c98] text-[0.75rem] font-medium tracking-[0.01em] uppercase">
                Create My Performance
              </span>
              <button
                type="button"
                className="ml-auto inline-flex items-center justify-center text-black"
                aria-label="Create a performance"
                onClick={() => {
                  closeMobile();
                  openModal("create-performance");
                }}
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            </div>

            {playlists && playlists.length > 0 && (
              <nav className="grid">
                {playlists.map((playlist) => (
                  <div key={playlist.id} className="relative group">
                    <Link
                      to={`/app/dashboard/playlist?playlist_id=${playlist.id}`}
                      className={cn(
                        "flex items-center justify-between rounded-[0.5rem] pr-2 text-black text-[0.875rem] font-medium transition-[background-color,color] duration-[180ms] ease-in-out hover:bg-[#f6f8fa]",
                        activePlaylistId === playlist.id && "bg-[#f6f8fa]"
                      )}
                      onClick={closeMobile}
                    >
                      <span className="inline-flex items-center gap-3 py-2 pl-2 min-w-0">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0">
                          <circle cx="10" cy="10" r="4" fill="#762a5c" />
                        </svg>
                        <span className="truncate">{playlist.name}</span>
                      </span>

                      {copiedShareId === playlist.id ? (
                        <span className="shrink-0 ml-1 text-[#22c55e]">
                          <CheckIcon />
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="shrink-0 ml-1 opacity-0 group-hover:opacity-100 transition-opacity text-[#868c98] hover:text-[#525866]"
                          aria-label="Performance options"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenMenuId(openMenuId === playlist.id ? null : playlist.id);
                          }}
                        >
                          <DotsThreeIcon className="h-5 w-5" />
                        </button>
                      )}
                    </Link>

                    {openMenuId === playlist.id && (
                      <PlaylistContextMenu
                        onClose={() => setOpenMenuId(null)}
                        onDeleteRequest={() => {
                          setOpenMenuId(null);
                          setDeleteTarget({ id: playlist.id, name: playlist.name });
                        }}
                        onShareRequest={() => void handleSharePlaylist(playlist.id)}
                      />
                    )}
                  </div>
                ))}
              </nav>
            )}
          </section>
        </div>

        <div className="px-4 pb-6">
          <nav className="grid">
            {isAdmin && (
              <NavLink
                to="/app/admin"
                className={({ isActive }) =>
                  cn(
                    "flex items-center justify-start rounded-[0.5rem] text-black text-[0.875rem] font-medium transition-[background-color,color] duration-[180ms] ease-in-out hover:bg-[#f6f8fa]",
                    isActive && "bg-[#f6f8fa]"
                  )
                }
                onClick={closeMobile}
              >
                {({ isActive }) => (
                  <span className="inline-flex items-center gap-3 py-2 pl-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className={cn("h-5 w-5 text-[#525866]", isActive && "text-[#762a5c]")}>
                      <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm40-96a8,8,0,0,1-8,8H136v24a8,8,0,0,1-16,0V128H96a8,8,0,0,1,0-16h24V88a8,8,0,0,1,16,0v24h24A8,8,0,0,1,168,120Z" />
                    </svg>
                    <span>Admin</span>
                  </span>
                )}
              </NavLink>
            )}
            {utilityNav.map((item) => (
              <NavLink
                key={item.label}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "flex items-center justify-start rounded-[0.5rem] text-black text-[0.875rem] font-medium transition-[background-color,color] duration-[180ms] ease-in-out hover:bg-[#f6f8fa]",
                    isActive && "bg-[#f6f8fa]"
                  )
                }
                onClick={closeMobile}
              >
                {({ isActive }) => (
                  <span className="inline-flex items-center gap-3 py-2 pl-2">
                    <item.Icon className={cn("h-5 w-5 text-[#525866]", isActive && "text-[#762a5c]")} />
                    <span>{item.label}</span>
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <button
            type="button"
            className="flex w-full items-center justify-start rounded-[0.5rem] text-black text-[0.875rem] font-medium transition-[background-color,color] duration-[180ms] ease-in-out hover:bg-[#f6f8fa]"
            disabled={loggingOut}
            onClick={() => void handleLogout()}
          >
            <span className="inline-flex items-center gap-3 py-2 pl-2">
              <SignOutIcon className="h-5 w-5 text-[#525866]" />
              <span>{loggingOut ? "Logging out..." : "Logout"}</span>
            </span>
          </button>
        </div>
      </aside>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Performance"
        description={`Are you sure you want to delete "${deleteTarget?.name ?? "this performance"}"? This action cannot be undone and all associated data will be permanently removed.`}
        confirmLabel="Delete Performance"
        loading={deleteLoading}
        onConfirm={() => void handleDeletePlaylist()}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
